import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Input, Modal, Spin, message } from 'antd';
import {
	CloseOutlined,
	DeleteOutlined,
	DownOutlined,
	EditOutlined,
	PlusOutlined,
	SearchOutlined,
	TagFilled,
	TagOutlined,
} from '@ant-design/icons';
import { isEqual } from 'lodash';
import { ColorPicker } from '../LabelCreation/ColorPicker';
import { EmptyState } from '../empty_state/EmptyState';
import useMountEffect from '../../hooks/useMountEffect';

// Minimal label shape the sidebar needs — both WorkOrderLabel and the parts
// Label satisfy it; entity-specific extras (counts etc.) ride along untyped
// and are read via the `getCount` accessor.
export interface SidebarLabel {
	id: number;
	label: string;
	color?: string;
}

export type LabelIconVariant = 'tag' | 'dot';

interface LabelsSidebarProps {
	onClose?: () => void;
	labels: SidebarLabel[];
	fetching: boolean;
	// Gates entering edit mode (create/rename/delete). When false the sidebar is
	// view-only.
	canModifyLabels: boolean;
	// Unpaginated catalogue read — the sidebar renders the whole list flat.
	fetchAllLabels: () => any;
	createLabel: (entity: { label: string; color: string }) => Promise<any>;
	updateLabel: (entity: SidebarLabel) => Promise<any>;
	deleteLabel: (entity: SidebarLabel) => Promise<any>;
	// Called after the catalogue is successfully created/updated/deleted — lets the
	// caller refetch dependents (table rows, client caches). May return a promise.
	onLabelsCatalogueSaved?: () => void | Promise<void>;
	// When provided, the per-label count becomes clickable (e.g. apply this label
	// as the index-page filter and close the sidebar).
	onLabelCountClick?: (label: SidebarLabel) => void;
	// When provided, the count renders next to each label in view mode.
	getCount?: (label: SidebarLabel) => number;
	// Visual language of the entity: work order labels use the tag icon, part
	// labels use the colored dot.
	iconVariant?: LabelIconVariant;
	// Color assigned to a freshly added row (each entity has its own palette).
	pickNewLabelColor: () => string;
	// Noun for the delete confirmation, e.g. "work orders" / "parts".
	deleteWarningEntityNoun?: string;
}

const labelChanged = (original: SidebarLabel, draft: SidebarLabel): boolean =>
	original.label.trim() !== draft.label.trim() || original.color !== draft.color;

const LabelIcon: React.FC<{ color?: string; variant: LabelIconVariant }> = ({ color, variant }) =>
	variant === 'dot' ? (
		<span
			className="inline-block h-3 w-3 flex-shrink-0 rounded-full"
			style={{ backgroundColor: color }}
		/>
	) : (
		<TagFilled className="flex-shrink-0 text-[14px]" style={{ color }} />
	);

export const LabelsSidebar: React.FC<LabelsSidebarProps> = ({
	onClose,
	labels,
	fetching,
	canModifyLabels,
	fetchAllLabels,
	createLabel,
	updateLabel,
	deleteLabel,
	onLabelsCatalogueSaved,
	onLabelCountClick,
	getCount,
	iconVariant = 'tag',
	pickNewLabelColor,
	deleteWarningEntityNoun = 'records',
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [draftLabels, setDraftLabels] = useState<SidebarLabel[]>([]);
	const [searchText, setSearchText] = useState('');
	const [showSearchInput, setShowSearchInput] = useState(false);
	const [colorPickerLabelId, setColorPickerLabelId] = useState<number | null>(null);
	const [saving, setSaving] = useState(false);
	const [initialLoadComplete, setInitialLoadComplete] = useState(false);

	// The sidebar renders the whole catalogue flat (no pagination), so load it all
	// once per open; search below filters client-side over this set.
	useMountEffect(() => {
		Promise.resolve(fetchAllLabels()).finally(() => setInitialLoadComplete(true));
	});

	const hasUnsavedChanges = useMemo(() => {
		if (!isEditing) return false;
		return !isEqual(labels, draftLabels);
	}, [isEditing, labels, draftLabels]);

	// Ids present on the backend when editing started — anything not in here is a
	// freshly-added draft row (used to autofocus it and to skip the delete
	// confirmation, since removing a row that was never saved deletes nothing).
	const originalIds = useMemo(() => new Set(labels.map((label) => label.id)), [labels]);

	const trimmedSearch = searchText.trim().toLowerCase();

	// Preserve the backend's order — it sorts by usage count, highest first
	// (work order labels and part labels alike). No client-side re-sort.
	const visibleLabels = useMemo(
		() =>
			trimmedSearch
				? labels.filter((label) => label.label.toLowerCase().includes(trimmedSearch))
				: labels,
		[labels, trimmedSearch]
	);

	// Empty-name rows are always visible so a row added mid-search can be typed into.
	const visibleDraftLabels = trimmedSearch
		? draftLabels.filter(
				(label) => !label.label.trim() || label.label.toLowerCase().includes(trimmedSearch)
		  )
		: draftLabels;

	const showDiscardConfirmation = (onDiscard: () => void) => {
		// Destructive action lives on the OK button so ESC / mask-dismiss resolves to
		// the safe "Keep Editing" path instead of silently discarding the user's edits.
		Modal.confirm({
			title: 'Discard changes?',
			content: 'Your unsaved changes will be lost.',
			okText: 'Discard Changes',
			okButtonProps: { danger: true },
			cancelText: 'Keep Editing',
			onOk: onDiscard,
		});
	};

	const resetViewState = () => {
		setSearchText('');
		setShowSearchInput(false);
	};

	const startEditing = () => {
		setDraftLabels(labels.map((label) => ({ ...label })));
		setIsEditing(true);
	};

	const exitEditMode = () => {
		setIsEditing(false);
		setDraftLabels([]);
		setColorPickerLabelId(null);
	};

	const cancelEditing = () => {
		if (hasUnsavedChanges) {
			showDiscardConfirmation(exitEditMode);
			return;
		}
		exitEditMode();
	};

	const requestClose = () => {
		if (saving) return;
		if (typeof onClose !== 'function') return;
		const close = () => {
			exitEditMode();
			resetViewState();
			onClose();
		};
		if (isEditing && hasUnsavedChanges) {
			showDiscardConfirmation(close);
			return;
		}
		close();
	};

	const updateDraftLabel = (labelId: number, patch: Partial<SidebarLabel>) => {
		setDraftLabels((current) =>
			current.map((label) => (label.id === labelId ? { ...label, ...patch } : label))
		);
	};

	const handleColorChange = (labelId: number, color: string) => {
		updateDraftLabel(labelId, { color });
		setColorPickerLabelId(null);
	};

	const closeColorPicker = () => setColorPickerLabelId(null);

	const deleteDraftLabel = (labelId: number) => {
		setDraftLabels((current) => current.filter((label) => label.id !== labelId));
	};

	const confirmDeleteDraftLabel = (label: SidebarLabel) => {
		// Rows that were never saved delete nothing on the backend — drop them silently.
		if (!originalIds.has(label.id)) {
			deleteDraftLabel(label.id);
			return;
		}
		const usageCount = getCount ? getCount(label) : 0;
		if (usageCount > 0) {
			const entityNounSingular = deleteWarningEntityNoun.replace(/s$/, '');
			Modal.confirm({
				title: `Remove label from all ${deleteWarningEntityNoun}?`,
				content: `"${label.label}" is currently applied to ${usageCount} ${
					usageCount === 1 ? entityNounSingular : deleteWarningEntityNoun
				}. Deleting it will permanently remove it from all of them. This cannot be undone.`,
				okText: 'Yes, Delete',
				okButtonProps: { danger: true },
				cancelText: 'Cancel',
				onOk: () => deleteDraftLabel(label.id),
			});
			return;
		}
		Modal.confirm({
			title: 'Delete label?',
			content: `Are you sure you want to delete "${label.label}"? It will be removed from all ${deleteWarningEntityNoun} when you save.`,
			okText: 'Delete',
			okButtonProps: { danger: true },
			cancelText: 'Cancel',
			onOk: () => deleteDraftLabel(label.id),
		});
	};

	const addDraftLabel = () => {
		setDraftLabels((current) => {
			const nextId = current.reduce((min, label) => Math.min(min, label.id), 0) - 1;
			return [...current, { id: nextId, label: '', color: pickNewLabelColor() }];
		});
	};

	// View mode "+ Add Label" — jump straight into edit mode with a fresh focused row.
	const startEditingWithNewRow = () => {
		startEditing();
		addDraftLabel();
	};

	const saveEdits = () => {
		const hasEmpty = draftLabels.some((label) => !label.label.trim());
		if (hasEmpty) {
			message.warning('Please fill in all label names or remove empty labels');
			return;
		}
		const originalById = new Map(labels.map((label) => [label.id, label]));
		const draftIds = new Set(draftLabels.map((label) => label.id));

		const toCreate = draftLabels.filter((label) => !originalById.has(label.id));
		const toUpdate = draftLabels.filter(
			(label) => originalById.has(label.id) && labelChanged(originalById.get(label.id), label)
		);
		const toDelete = labels.filter((label) => !draftIds.has(label.id));

		if (toCreate.length === 0 && toUpdate.length === 0 && toDelete.length === 0) {
			exitEditMode();
			resetViewState();
			return;
		}

		const operations = [
			...toCreate.map((label) => createLabel({ label: label.label.trim(), color: label.color })),
			...toUpdate.map((label) =>
				updateLabel({ id: label.id, label: label.label.trim(), color: label.color })
			),
			...toDelete.map((label) => deleteLabel(label)),
		];

		setSaving(true);
		Promise.all(operations)
			.then(() => fetchAllLabels())
			.then(() => (onLabelsCatalogueSaved ? onLabelsCatalogueSaved() : undefined))
			.then(() => {
				exitEditMode();
				resetViewState();
				message.success('Labels saved successfully');
			})
			.catch(() => {
				message.error('Failed to save labels. Please try again.');
			})
			.finally(() => setSaving(false));
	};

	const renderEmptyState = () => (
		<EmptyState
			body={
				searchText.trim()
					? 'No labels match your search'
					: canModifyLabels
					? 'No labels yet. Click edit to create labels'
					: 'No labels yet'
			}
			height="auto"
		/>
	);

	const openSearchInput = () => setShowSearchInput(true);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) =>
		setSearchText(event.target.value);

	const handleSearchBlur = () => {
		if (!searchText.trim()) setShowSearchInput(false);
	};

	const addLabelButton = (onClick: () => void) =>
		canModifyLabels ? (
			// Plain button (not antd) so the plus icon gets the exact same 8px inset as
			// the row icons above it — antd's Button border + padding shift it off-grid.
			<button
				type="button"
				onClick={onClick}
				className="bodymedium flex h-[29px] cursor-pointer items-center gap-2 self-start rounded-[2px] border-0 bg-transparent px-2 py-1 text-buyer"
			>
				<PlusOutlined className="text-[14px]" />
				<span>Add Label</span>
			</button>
		) : null;

	const renderViewRow = (label: SidebarLabel) => {
		const count = getCount ? getCount(label) : undefined;
		return (
			<div
				key={label.id}
				className="cursor-pointer px-2 py-1 hover:bg-grey-3"
				onClick={() => onLabelCountClick(label)}
			>
				<span className="bodyregular flex items-center justify-between gap-2 align-middle text-character-primary">
					<span className="inline-flex items-center gap-2 overflow-hidden">
						<LabelIcon color={label.color} variant={iconVariant} />
						<span className="overflow-hidden text-ellipsis whitespace-nowrap">{label.label}</span>
					</span>
					{typeof count === 'number' ? (
						<span className="flex-shrink-0 cursor-pointer text-character-secondary hover:underline">
							({count})
						</span>
					) : null}
				</span>
			</div>
		);
	};

	const viewModeBody =
		!initialLoadComplete || fetching ? (
			<div className="flex justify-center py-6">
				<Spin size="small" />
			</div>
		) : (
			<div className="flex flex-col gap-2 px-2">
				{visibleLabels.length === 0 ? renderEmptyState() : visibleLabels.map(renderViewRow)}
				{addLabelButton(startEditingWithNewRow)}
			</div>
		);

	const renderEditRow = (label: SidebarLabel) => {
		const isNewLabel = !originalIds.has(label.id);
		return (
			<div key={label.id} className="flex items-center gap-2.5 px-2 py-[3px]">
				<ColorPicker
					currentColor={label.color}
					onColorChange={(color) => handleColorChange(label.id, color)}
					onCancel={closeColorPicker}
					visible={colorPickerLabelId === label.id}
					onVisibleChange={(open) => setColorPickerLabelId(open ? label.id : null)}
					buttonDisplay={
						<span className="inline-flex h-6 shrink-0 cursor-pointer items-center gap-2 rounded-[2px] border border-solid border-grey-4 bg-white px-2 shadow-[0px_2px_0px_rgba(0,0,0,0.02)]">
							<LabelIcon color={label.color} variant={iconVariant} />
							<DownOutlined className="text-[10px] text-grey-7" />
						</span>
					}
				/>
				<Input
					value={label.label}
					onChange={(event) => updateDraftLabel(label.id, { label: event.target.value })}
					className="h-6 flex-1 !rounded-[2px] !border-grey-4"
					placeholder="Label name"
					size="small"
					autoFocus={isNewLabel}
				/>
				<DeleteOutlined
					onClick={() => confirmDeleteDraftLabel(label)}
					className="shrink-0 cursor-pointer text-[12px] text-character-primary"
				/>
			</div>
		);
	};

	const editModeBody = (
		<div className="flex flex-col gap-2 px-2">
			{visibleDraftLabels.map(renderEditRow)}
			{addLabelButton(addDraftLabel)}
		</div>
	);

	const footerButtons = (
		<div className="flex w-full items-center gap-2">
			<Button onClick={cancelEditing} disabled={saving} className="flex-1 !rounded-[2px]">
				CANCEL
			</Button>
			<Button
				type="primary"
				onClick={saveEdits}
				disabled={!hasUnsavedChanges || saving}
				loading={saving}
				className="flex-1 !rounded-[2px]"
			>
				SAVE
			</Button>
		</div>
	);

	const title = (
		// Fixed 32px row (+ the drawer header's 8px paddings = the design's 48px header)
		// so the header doesn't grow/shrink when the search input swaps in for the
		// title — the input is capped at 24px and centers within the row.
		<div className="flex h-8 w-full items-center gap-2">
			<TagOutlined className="h5medium" />
			{showSearchInput ? (
				// Search takes the title's slot — the 380px header can't fit both without
				// the title wrapping to a second line and stretching the 48px header.
				<Input
					allowClear
					size="small"
					placeholder="Search labels..."
					prefix={<SearchOutlined />}
					value={searchText}
					onChange={handleSearchChange}
					onBlur={handleSearchBlur}
					autoFocus
					className="!h-6 min-w-0 flex-1"
				/>
			) : (
				<>
					<span className="h5medium flex-1 whitespace-nowrap text-character-primary">
						{isEditing ? 'Edit Labels' : 'Labels'}
					</span>
					<div className="flex items-center gap-4 pr-3">
						<SearchOutlined
							onClick={openSearchInput}
							className="cursor-pointer text-[16px] text-character-primary"
						/>
						{!isEditing && canModifyLabels ? (
							<EditOutlined
								onClick={startEditing}
								className="cursor-pointer text-[16px] text-character-primary"
							/>
						) : null}
					</div>
				</>
			)}
			{typeof onClose === 'function' ? (
				<div className="border-0 border-l border-solid border-grey-5 pl-3">
					<CloseOutlined
						onClick={requestClose}
						className="cursor-pointer text-[16px] text-character-primary"
					/>
				</div>
			) : null}
		</div>
	);

	if (typeof onClose === 'function') {
		return (
			<Drawer
				title={title}
				visible
				width={380}
				onClose={requestClose}
				maskClosable={!saving}
				placement="right"
				destroyOnClose
				closable={false}
				headerStyle={{ padding: '8px 16px' }}
				bodyStyle={{ padding: '16px 8px' }}
				footer={isEditing ? footerButtons : null}
				footerStyle={{ padding: '12px 16px' }}
			>
				<Spin spinning={saving}>{isEditing ? editModeBody : viewModeBody}</Spin>
			</Drawer>
		);
	}

	return (
		<div className="mx-auto my-6 max-w-[640px] bg-white p-6">
			<div className="mb-4 flex items-center justify-between gap-2">{title}</div>
			<Spin spinning={saving}>{isEditing ? editModeBody : viewModeBody}</Spin>
			{isEditing ? (
				<div className="mt-6 border-t border-solid border-grey-4 pt-4">{footerButtons}</div>
			) : null}
		</div>
	);
};
